import { z } from "zod";
import { ENCOUNTER_DIAGNOSES_MAX } from "./encounter-diagnoses.js";
import { ClinicOrderSetOptionSchema, LabOrderSetRefSchema } from "./form-order-sets.js";
import { LetterDraftSchema } from "./letters.js";
import { ModelReleasePinSchema } from "./model-governance.js";
import { LabOrderFastingSchema } from "./orders.js";
import { VisitContextRefSchema } from "./visit-context.js";
/** Maximum completed encounter transcript; oversize input must fail, never truncate. */
export const DEMO_DICTATION_TRANSCRIPT_MAX_CHARS = 240_000;
/** Normalize transport whitespace without changing sentence or negation boundaries. */
export function normalizeDemoClinicalText(value) {
    return value
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => line.replace(/[ \t]+/g, " ").trim())
        .join("\n")
        .trim();
}
const EncounterTextSchema = z.string().trim().max(12_000);
const ListSchema = z.array(z.string().trim().min(1).max(500)).max(30);
const TranscriptTextSchema = z.string().max(DEMO_DICTATION_TRANSCRIPT_MAX_CHARS).trim().min(1);
export const PatientSexSchema = z.enum(["male", "female"]);
export const SuggestedAssessmentSchema = z
    .string()
    .trim()
    // Fixed wording (39 characters) plus two clauses of at most 240 characters.
    .max(519)
    .regex(
// Provider-compatible consuming pattern: internal dots must be between digits.
/^Patient presents with (?:[^.\r\n]|\d(?:\.\d)+)+, likely due to (?:[^.\r\n]|\d(?:\.\d)+)+\.$/, 'Assessment must be one line in the format "Patient presents with [X], likely due to [Y]."')
    .refine(
// Enforce individual clause lengths locally on every parsed response. Zod omits
// this refinement from JSON Schema; OpenAI does not support regex lookarounds.
(value) => /^Patient presents with [^\r\n]{1,240}, likely due to [^\r\n]{1,240}\.$/.test(value), "Assessment presentation and cause must each contain 1 to 240 characters");
export const SuggestedDiagnosisCodeSchema = z
    .object({
    code: z
        .string()
        .trim()
        .regex(/^[A-Z][0-9][A-Z0-9](?:\.[A-Z0-9]{1,4})?$/, "Invalid ICD-10-CM code"),
    description: z.string().trim().min(1).max(200),
    system: z.literal("ICD-10-CM"),
})
    .strict();
export const EncounterPatientProfileSchema = z.object({
    id: z.string().min(1).max(100),
    displayName: z.string().trim().min(1).max(200),
    dob: z.iso.date(),
    sex: PatientSexSchema,
    mrn: z.string().trim().min(1).max(80),
    /**
     * The patient health number is the provincial/federal coverage member ID.
     * Persisted charts hydrate it from the workspace's healthCardNumber (the
     * same value the referral package and vault fill runtime print); synthetic
     * demo patients carry it directly.
     */
    healthNumber: z.string().trim().max(20),
    healthVersionCode: z.string().trim().max(4),
    phone: z.string().trim().min(1).max(40),
    address: z.string().trim().min(1).max(400),
    allergies: ListSchema,
    medications: ListSchema,
    problems: ListSchema,
});
export const EncounterSoapNoteSchema = z.object({
    subjective: EncounterTextSchema,
    objective: EncounterTextSchema,
    assessment: EncounterTextSchema,
    plan: EncounterTextSchema,
});
/** Explicitly empty fields mean there is no manual intent; generated A/P is not a fallback. */
export const ClinicianIntentSchema = z
    .object({ assessment: EncounterTextSchema, plan: EncounterTextSchema })
    .strict();
/** Exact current-transcript evidence for explicitly spoken clinician decisions. */
export const DictationClinicianSourceQuotesSchema = z
    .object({
    assessment: z.array(z.string().trim().min(1).max(4_000)).max(12),
    plan: z.array(z.string().trim().min(1).max(4_000)).max(12),
})
    .strict();
/** Expand only unique, token-aligned evidence back to complete source utterances. */
export function expandDictationIntentQuotes(transcript, quotes) {
    const boundaries = [...transcript.matchAll(/[.!?](?=\s|$)|\r?\n/g)].map((match) => (match.index ?? 0) + match[0].length);
    // Internal decimal, fraction, contraction and word separators are part of
    // the token: expansion cannot legitimize a partial dose or word.
    const tokens = [
        ...transcript.matchAll(/[\p{L}\p{N}\p{M}]+(?:[.'\u2019/,+-][\p{L}\p{N}\p{M}]+)*/gu),
    ];
    const expanded = [];
    let previousEnd = -1;
    for (const quote of quotes) {
        const offset = transcript.indexOf(quote);
        const quoteEnd = offset + quote.length;
        if (!quote || offset < 0 || transcript.indexOf(quote, offset + 1) >= 0)
            return undefined;
        if (tokens.some((token) => {
            const start = token.index ?? 0;
            const end = start + token[0].length;
            return (start < offset && offset < end) || (start < quoteEnd && quoteEnd < end);
        }))
            return undefined;
        let start = 0;
        let end = transcript.length;
        for (const boundary of boundaries) {
            if (boundary <= offset)
                start = boundary;
            if (boundary >= quoteEnd) {
                end = boundary;
                break;
            }
        }
        const complete = transcript.slice(start, end).trim();
        const completeStart = transcript.indexOf(complete, start);
        if (complete.length > 4_000 || !isCompleteDictationIntentQuote(transcript, complete)) {
            return undefined;
        }
        if (expanded.at(-1) === complete)
            continue;
        if (completeStart < previousEnd)
            return undefined;
        expanded.push(complete);
        previousEnd = completeStart + complete.length;
    }
    return expanded;
}
function hasPatientAttribution(transcript, offset, end) {
    let currentSpeaker = "";
    for (const match of transcript.matchAll(/(?:^|[\r\n]|[.!?;]\s+)(patient|caregiver|parent|family member|clinician|doctor|physician|provider|nurse)\s*:/giu)) {
        const labelStart = (match.index ?? 0) + match[0].indexOf(match[1] ?? "");
        if (labelStart >= end)
            break;
        const isPatient = /^(?:patient|caregiver|parent|family member)$/i.test(match[1] ?? "");
        if (labelStart <= offset)
            currentSpeaker = isPatient ? "patient" : "clinician";
        else if (isPatient)
            return true;
    }
    return currentSpeaker === "patient";
}
/** Reject clipped clauses that could drop a negation, condition, or speaker attribution. */
export function isCompleteDictationIntentQuote(transcript, quote) {
    const offset = transcript.indexOf(quote);
    if (offset < 0 || transcript.indexOf(quote, offset + 1) >= 0)
        return false;
    if (hasPatientAttribution(transcript, offset, offset + quote.length))
        return false;
    const before = transcript.slice(0, offset);
    let clauseStart = 0;
    for (const match of transcript.matchAll(/[.!?](?=\s|$)|\r?\n/g)) {
        const boundaryEnd = (match.index ?? 0) + match[0].length;
        if (boundaryEnd > offset)
            break;
        clauseStart = boundaryEnd;
    }
    const prefix = before.slice(clauseStart).trim();
    const patientLabel = /^(?:patient|caregiver|parent|family member)\s*:/i;
    if (patientLabel.test(prefix) || patientLabel.test(quote))
        return false;
    if (prefix && !/^[\p{L}][\p{L}\p{N} _-]{0,79}:$/u.test(prefix))
        return false;
    const after = transcript.slice(offset + quote.length);
    return (after.length === 0 ||
        (/[.!?]$/.test(quote) && /^\s/.test(after)) ||
        /^[ \t]*(?:[.!?](?=\s|$)|\r?\n|$)/.test(after));
}
export const DictatedClinicianIntentSchema = ClinicianIntentSchema.extend({
    sourceQuotes: DictationClinicianSourceQuotesSchema,
})
    .strict()
    .superRefine((value, context) => {
    for (const section of ["assessment", "plan"]) {
        if (value[section] !== value.sourceQuotes[section].join("\n")) {
            context.addIssue({
                code: "custom",
                message: "Dictated clinician intent must preserve its exact source quotes",
                path: [section],
            });
        }
    }
});
export const DictationContextSchema = z
    .object({
    transcript: TranscriptTextSchema,
    segmentCount: z.number().int().min(1).max(2_000),
})
    .strict();
/** A distinct concern discussed at this visit, not an individual associated symptom. */
export const EncounterProblemSchema = z.object({
    id: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(160),
});
export const ProblemSelectionSchema = z.object({
    problemId: EncounterProblemSchema.shape.id,
    diagnosisLabel: z.string().trim().min(1).max(160),
    confidence: z.enum(["high", "moderate", "low"]),
});
export const EncounterSnapshotSchema = z
    .object({
    patient: EncounterPatientProfileSchema,
    patientAgeYears: z.number().int().min(0).max(130),
    encounterId: z.string().min(1).max(100),
    /** Clinic-local calendar reference for relative dates in the active encounter. */
    encounterDate: z.iso.date().optional(),
    chiefComplaint: z.string().trim().min(1).max(500),
    note: EncounterSoapNoteSchema,
    clinicianIntent: ClinicianIntentSchema.optional(),
    /** Known groups keep their identity across later dictation and wording changes. */
    problems: z.array(EncounterProblemSchema).max(ENCOUNTER_DIAGNOSES_MAX).optional(),
    /** Explicit clinician choices; proposals in a differential do not select themselves. */
    problemSelections: z.array(ProblemSelectionSchema).max(ENCOUNTER_DIAGNOSES_MAX).optional(),
    /** Draft only this problem while retaining the whole encounter's safety context. */
    activeProblemId: EncounterProblemSchema.shape.id.optional(),
    /** A clinician edit to this problem's own heading-delimited assessment/plan. */
    activeProblemClinicianIntent: ClinicianIntentSchema.optional(),
    dictationContext: DictationContextSchema.optional(),
    /**
     * The clinic's published order sets, which a plan may apply to a lab action. Set by the server
     * only (whatever a client sends is replaced), and never treated as evidence about the patient.
     */
    clinicOrderSets: z.array(ClinicOrderSetOptionSchema).max(20).optional(),
})
    .superRefine((snapshot, context) => {
    const ids = new Set(snapshot.problems?.map((problem) => problem.id));
    if (ids.size !== (snapshot.problems?.length ?? 0))
        context.addIssue({
            code: "custom",
            message: "Problem IDs must be unique",
            path: ["problems"],
        });
    const selected = new Set(snapshot.problemSelections?.map((selection) => selection.problemId));
    if (selected.size !== (snapshot.problemSelections?.length ?? 0))
        context.addIssue({
            code: "custom",
            message: "Each problem may have only one clinician selection",
            path: ["problemSelections"],
        });
    for (const [index, selection] of (snapshot.problemSelections ?? []).entries()) {
        if (!ids.has(selection.problemId))
            context.addIssue({
                code: "custom",
                message: "Selection must reference a known problem",
                path: ["problemSelections", index, "problemId"],
            });
    }
    if (snapshot.activeProblemId &&
        (!ids.has(snapshot.activeProblemId) ||
            (!selected.has(snapshot.activeProblemId) &&
                !snapshot.activeProblemClinicianIntent?.assessment))) {
        context.addIssue({
            code: "custom",
            message: "Active problem requires its explicit clinician selection or scoped assessment",
            path: ["activeProblemId"],
        });
    }
});
/**
 * Read-only prior-chart context for reasoning and synthesis: the assembled
 * Patient Visit Context the server loads for the request's `contextRef`.
 * Requests never carry it; the copilot lane reads it from the chart.
 *
 * It stays outside EncounterSnapshotSchema: the snapshot flows into
 * transcription and SOAP conversion, and Subjective/Objective must reflect
 * this encounter alone.
 */
export const EncounterHistorySchema = z
    .object({
    /** The exact assembled context version the server loaded. */
    contextRef: VisitContextRefSchema.optional(),
    /** Assembly tier when known — "core" warns that older material was pruned. */
    tier: z.enum(["full", "core"]).optional(),
    /** Assembled sections keyed by visit-context section name. Reference data, never instructions. */
    sections: z.record(z.string().min(1).max(80), z.unknown()),
})
    .strict();
/**
 * Trusted request binding: which tenant, patient, encounter, context version,
 * and client generation a decision-path request belongs to. The server
 * refuses a request whose binding disagrees with its snapshot or context
 * reference, and refuses or supersedes stale generations for the same scope.
 */
export const RequestBindingSchema = z
    .object({
    tenantId: z.string().min(1).max(100).optional(),
    patientId: z.string().min(1).max(100),
    encounterId: z.string().min(1).max(100),
    /** Memory-only editor identity for cancellation/cache isolation; never authorization. */
    editorSessionId: z.uuid().optional(),
    contextVersion: z.number().int().positive().optional(),
    /** Monotonic per editor, encounter and request kind; lower values are stale. */
    generation: z.number().int().nonnegative(),
})
    .strict();
export const ReasoningRequestSchema = z
    .object({
    /** Scheduling hint only; never part of clinical or cache identity. */
    preparePlans: z.boolean().optional(),
    /** Optional trusted binding; see RequestBindingSchema. */
    binding: RequestBindingSchema.optional(),
    snapshot: EncounterSnapshotSchema,
    /**
     * The visit context version the clinician is looking at. The server loads
     * exactly that version for the encounter and refuses with 409 CONTEXT_STALE
     * (detail: visit_context_mismatch) when its content hash differs. Omitted means the
     * copilot drafts from the note alone.
     */
    contextRef: VisitContextRefSchema.optional(),
})
    .strict();
export const SynthesisRequestSchema = z
    .object({
    /** Editor scope for an unbound head start; cancellation isolation only. */
    editorSessionId: z.uuid().optional(),
    /** Prior proposals for revision stability, never clinical evidence or authority. */
    previousActions: z.lazy(() => z.array(PlanActionSchema).max(20)).optional(),
    /** Optional trusted binding; see RequestBindingSchema. */
    binding: RequestBindingSchema.optional(),
    snapshot: EncounterSnapshotSchema,
    /** See ReasoningRequestSchema.contextRef. */
    contextRef: VisitContextRefSchema.optional(),
})
    .strict();
/** Sections of the encounter record the incremental state tracker fingerprints independently. */
export const EncounterStateSectionSchema = z.enum([
    "chief_complaint",
    "subjective",
    "objective",
    "assessment",
    "plan",
    "dictation",
    "clinician_intent",
    "patient_allergies",
    "patient_medications",
    "patient_problems",
]);
/**
 * Compact, deterministic facts copied from the current note. The extractor
 * quotes what the record says (durations as written, vitals as written,
 * which listed medications and allergies the note mentions) and never infers
 * a value; the warning terms are a fixed vocabulary hit, not a diagnosis.
 */
export const EncounterFactsSchema = z
    .object({
    extractor: z.literal("deterministic-v1"),
    durations: z.array(z.string().min(1).max(80)).max(20),
    vitals: z
        .array(z
        .object({
        name: z.enum([
            "temperature",
            "heart_rate",
            "blood_pressure",
            "spo2",
            "respiratory_rate",
        ]),
        value: z.string().min(1).max(40),
    })
        .strict())
        .max(12),
    medicationsMentioned: z.array(z.string().min(1).max(200)).max(40),
    allergiesMentioned: z.array(z.string().min(1).max(200)).max(40),
    redFlagTerms: z.array(z.string().min(1).max(80)).max(20),
    sectionLengths: z.record(EncounterStateSectionSchema, z.number().int().nonnegative()),
})
    .strict();
export const EncounterStateRequestSchema = z
    .object({
    binding: RequestBindingSchema,
    snapshot: EncounterSnapshotSchema,
    /** Required to verify a binding that names a chart context version. */
    contextRef: VisitContextRefSchema.optional(),
})
    .strict();
/**
 * What the tracker decided about this observation. `version` advances only on
 * a material, settled change; `trigger` tells the client whether to do nothing,
 * resend the same snapshot after `retryAfterMs`, or request reasoning now.
 */
export const EncounterStateResponseSchema = z
    .object({
    version: z.number().int().positive(),
    stateHash: z.string().regex(/^[a-f0-9]{16}$/),
    changedSections: z.array(EncounterStateSectionSchema).max(10),
    material: z.boolean(),
    trigger: z.enum(["none", "debounce", "reprocess"]),
    retryAfterMs: z.number().int().nonnegative().optional(),
    facts: EncounterFactsSchema,
    /** Echo of the accepted binding generation. */
    generation: z.number().int().nonnegative(),
})
    .strict();
export const ClinicalTopicSchema = z.enum([
    "timeline",
    "symptom_character",
    "associated_symptoms",
    "red_flags",
    "functional_impact",
    "risk_context",
    "focused_exam",
    "treatment_response",
    "relevant_history",
]);
export const DiscussionReadinessSchema = z
    .object({
    ready: z.boolean(),
    minimumTopics: z.literal(2),
    topicCount: z.number().int().min(0).max(ClinicalTopicSchema.options.length),
    topics: z.array(ClinicalTopicSchema).max(ClinicalTopicSchema.options.length),
    message: z.string().trim().min(1).max(500),
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.topics).size !== value.topics.length) {
        context.addIssue({
            code: "custom",
            message: "Discussion topics must be unique",
            path: ["topics"],
        });
    }
    if (value.topicCount !== value.topics.length) {
        context.addIssue({
            code: "custom",
            message: "Topic count must match the number of discussion topics",
            path: ["topicCount"],
        });
    }
    if (value.ready !== value.topicCount >= value.minimumTopics) {
        context.addIssue({
            code: "custom",
            message: "Readiness must reflect the minimum topic threshold",
            path: ["ready"],
        });
    }
});
const ReasoningFollowUpIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const DictationFollowUpProposalIdSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const ReasoningFollowUpSchema = z
    .object({
    id: ReasoningFollowUpIdSchema,
    kind: z.enum(["ask", "examine"]),
    prompt: z.string().trim().min(1).max(500),
    noteSection: z.enum(["subjective", "objective"]),
})
    .strict()
    .superRefine((followUp, context) => {
    const expectedSection = followUp.kind === "ask" ? "subjective" : "objective";
    if (followUp.noteSection !== expectedSection) {
        context.addIssue({
            code: "custom",
            message: `${followUp.kind} follow-ups must target ${expectedSection}`,
            path: ["noteSection"],
        });
    }
});
export const DictationFollowUpContextSchema = z
    .object({
    branchLabel: z.string().trim().min(1).max(160),
    followUps: z.array(ReasoningFollowUpSchema).min(1).max(12),
})
    .strict()
    .superRefine((context, refinement) => {
    if (new Set(context.followUps.map((followUp) => followUp.id)).size !== context.followUps.length) {
        refinement.addIssue({
            code: "custom",
            message: "Follow-up IDs must be unique within dictation context",
            path: ["followUps"],
        });
    }
});
export const DictationFollowUpProposalSchema = z
    .object({
    followUpId: DictationFollowUpProposalIdSchema,
    answer: z
        .string()
        .min(1)
        .max(4_000)
        .refine((answer) => answer.trim().length > 0, "Follow-up answer must contain text"),
    chartText: z.string().trim().min(1).max(2_000).optional(),
})
    .strict();
export const FollowUpDocumentationAnswerSchema = z
    .object({
    followUpId: DictationFollowUpProposalIdSchema,
    response: z
        .string()
        .min(1)
        .max(4_000)
        .refine((response) => response.trim().length > 0, "Follow-up response must contain clinician-reviewed text"),
})
    .strict();
export const FollowUpDocumentationRequestSchema = z
    .object({
    followUpContext: DictationFollowUpContextSchema,
    answers: z.array(FollowUpDocumentationAnswerSchema).min(1).max(12),
})
    .strict()
    .superRefine((request, context) => {
    const knownIds = new Set(request.followUpContext.followUps.map((followUp) => followUp.id));
    const seenIds = new Set();
    for (const [index, answer] of request.answers.entries()) {
        if (seenIds.has(answer.followUpId)) {
            context.addIssue({
                code: "custom",
                message: "Follow-up documentation answer IDs must be unique",
                path: ["answers", index, "followUpId"],
            });
        }
        seenIds.add(answer.followUpId);
        if (!knownIds.has(answer.followUpId)) {
            context.addIssue({
                code: "custom",
                message: "Follow-up documentation answers must reference a supplied follow-up",
                path: ["answers", index, "followUpId"],
            });
        }
    }
});
/** Identifier-only scope for encounter-bound copilot operations without a note snapshot. */
export const EncounterCopilotScopeSchema = z
    .object({
    patientId: RequestBindingSchema.shape.patientId,
    encounterId: RequestBindingSchema.shape.encounterId,
})
    .strict();
export const EncounterCopilotDictationSessionRequestSchema = z
    .object({ encounter: EncounterCopilotScopeSchema })
    .strict();
export const EncounterCopilotFollowUpDocumentationRequestSchema = FollowUpDocumentationRequestSchema.safeExtend({ encounter: EncounterCopilotScopeSchema });
export const FollowUpDocumentationSuggestionSchema = z
    .object({
    followUpId: DictationFollowUpProposalIdSchema,
    noteSection: z.enum(["subjective", "objective"]),
    chartText: z.string().trim().min(1).max(2_000),
})
    .strict();
export const ReasoningBranchSchema = z
    .object({
    label: z.string().trim().min(1).max(160),
    likelihood: z.enum(["leading", "possible", "less_likely", "must_not_miss"]),
    confidence: z.enum(["high", "moderate", "low"]),
    supportingFacts: ListSchema,
    contradictingFacts: ListSchema,
    missingInformation: ListSchema,
    nextQuestions: ListSchema,
    followUps: z.array(ReasoningFollowUpSchema).max(2),
    conditionalPath: z.string().trim().min(1).max(1_000),
    suggestedActionKinds: z
        .array(z.enum([
        "history",
        "exam",
        "point_of_care_test",
        "lab",
        "imaging",
        "referral",
        "medication",
        "supportive_care",
        "follow_up",
        "urgent_escalation",
    ]))
        .max(12),
})
    .superRefine((branch, context) => {
    if (new Set(branch.followUps.map((followUp) => followUp.id)).size !== branch.followUps.length) {
        context.addIssue({
            code: "custom",
            message: "Follow-up IDs must be unique within a reasoning branch",
            path: ["followUps"],
        });
    }
});
export const CopilotEngineSchema = z.object({
    /** Who produced the draft. `cerebras` is the fast-inference route; `deterministic_demo` is the offline fallback. */
    provider: z.enum(["openai", "cerebras", "deterministic_demo"]),
    model: z.string().trim().min(1).max(120),
    liveModel: z.boolean(),
    notice: z.string().trim().min(1).max(500),
    /** Configured release provenance for the model-produced draft; never grants approval. */
    release: ModelReleasePinSchema.optional(),
});
// Diagnostic metadata only. Provider response bodies can echo patient data or credentials.
export const DictationFormattingFailureSchema = z
    .object({
    provider: z.enum(["openai", "assemblyai", "cerebras", "deterministic_demo"]),
    model: z.string().trim().min(1).max(120),
    kind: z.enum([
        "http_error",
        "timeout",
        "network_error",
        "invalid_response",
        "invalid_soap",
        "refused",
        "output_limit",
    ]),
    message: z.string().min(1).max(240),
    httpStatus: z.number().int().min(100).max(599).optional(),
    requestId: z
        .string()
        .regex(/^(?:[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}|req_[a-f0-9]{16,64})$/i)
        .optional(),
})
    .strict();
export const FollowUpDocumentationResponseSchema = z
    .object({
    suggestions: z.array(FollowUpDocumentationSuggestionSchema).max(12),
    engine: CopilotEngineSchema,
    formattingFailure: DictationFormattingFailureSchema.optional(),
})
    .strict()
    .superRefine((response, context) => {
    if (new Set(response.suggestions.map((suggestion) => suggestion.followUpId)).size !==
        response.suggestions.length) {
        context.addIssue({
            code: "custom",
            message: "Follow-up documentation suggestion IDs must be unique",
            path: ["suggestions"],
        });
    }
});
export const ReasoningProblemSchema = EncounterProblemSchema.extend({
    branches: z.array(ReasoningBranchSchema).max(4),
});
export const EncounterReasoningGraphSchema = z.object({
    summary: z.string().trim().min(1).max(1_500),
    branches: z.array(ReasoningBranchSchema).max(4),
    problems: z.array(ReasoningProblemSchema).max(ENCOUNTER_DIAGNOSES_MAX).optional(),
    safetyConcerns: ListSchema,
    readiness: DiscussionReadinessSchema,
    engine: CopilotEngineSchema,
});
/** One ranked differential branch without its evidence: what a clinician picks from. */
export const ReasoningListBranchSchema = z.object({
    label: ReasoningBranchSchema.shape.label,
    likelihood: ReasoningBranchSchema.shape.likelihood,
    confidence: ReasoningBranchSchema.shape.confidence,
});
export const ReasoningListProblemSchema = EncounterProblemSchema.extend({
    branches: z.array(ReasoningListBranchSchema).max(4),
});
/**
 * The fast first phase of a differential: the visit's problems and ranked
 * branches only. The full graph that follows carries evidence written for
 * exactly these branches, so its labels and confidences are the same.
 */
export const ReasoningListSchema = z.object({
    branches: z.array(ReasoningListBranchSchema).max(4),
    problems: z.array(ReasoningListProblemSchema).max(ENCOUNTER_DIAGNOSES_MAX).optional(),
    readiness: DiscussionReadinessSchema,
    engine: CopilotEngineSchema,
});
/**
 * Complete AI-drafted prescription proposal carried by a prescription-kind
 * plan action. Mirrors the printable-prescription form exactly so the draft
 * lands in editable fields for physician review; the physician approves,
 * edits, or discards it, and signing remains a clinician-only act.
 */
export const PrescriptionDraftSchema = z.object({
    /** Medication, strength, and form (e.g. "Penicillin V potassium 300 mg oral tablet"). */
    medicationDisplay: z.string().trim().min(1).max(200),
    /** Full directions: dose, route, frequency, and duration. */
    sig: z.string().trim().min(1).max(500),
    quantity: z.number().positive().max(5_000),
    quantityUnit: z.string().trim().min(1).max(40),
    refills: z.number().int().min(0).max(12),
    indication: z.string().trim().min(1).max(200),
    substitutionAllowed: z.boolean(),
});
/**
 * Structured laboratory content carried with a review-only encounter proposal.
 * The persisted encounter bridge turns this into a governed order only after
 * the clinician applies the plan and reviews the exact server projection.
 */
export const LabOrderTestDraftSchema = z
    .object({
    code: z.string().trim().min(1).max(80),
    display: z.string().trim().min(1).max(240),
    codeSystem: z.enum(["LOINC", "local", "free_text"]),
    reason: z.string().trim().min(1).max(1_000),
    specimen: z.string().trim().min(1).max(160),
    fasting: LabOrderFastingSchema,
})
    .strict();
export const DraftedLabOrderSchema = z
    .object({
    tests: z.array(LabOrderTestDraftSchema).min(1).max(40),
    diagnosisCodes: z.array(z.string().trim().min(1).max(80)).min(1).max(20),
    /** The clinic order set the server expanded these tests from; the model never sets it. */
    orderSet: LabOrderSetRefSchema.optional(),
})
    .strict();
export const PlanActionSchema = z.object({
    kind: z.enum([
        "treatment",
        "lab",
        "imaging",
        "referral",
        "prescription",
        "patient_instruction",
        "letter",
        "follow_up",
    ]),
    title: z.string().trim().min(1).max(200),
    detail: z.string().trim().min(1).max(1_500),
    rationale: z.string().trim().min(1).max(1_000),
    priority: z.enum(["routine", "soon", "urgent"]),
    requiresClinicianDecision: z.boolean(),
    /**
     * True when the model would only "consider" this order (contingent on
     * non-response, recurrence, or patient factors) rather than commit to it.
     * Conditional actions are surfaced as physician quick-adds, never included
     * in the committed draft set until a physician promotes them. This is a
     * packaging flag: it must not change what the model recommends.
     */
    conditional: z.boolean().default(false),
    /** Present on prescription-kind actions when the AI drafted the full order. */
    prescriptionDraft: PrescriptionDraftSchema.nullish(),
    /** Present on lab-kind actions when the AI drafted exact reviewable tests. */
    labOrderDraft: DraftedLabOrderSchema.nullish(),
    /** Reviewable wording for a letter; it confers no signing authority. */
    letterDraft: LetterDraftSchema.nullish(),
    /** Optional document wording is independent of the complete clinical decision. */
    narrativeStatus: z.enum(["pending", "ready", "failed"]).optional(),
});
export const LabPlanActionSchema = PlanActionSchema.extend({
    kind: z.literal("lab"),
});
export const DiagnosisCandidateSchema = z.object({
    label: z.string().trim().min(1).max(200),
    confidence: z.enum(["high", "moderate", "low"]),
    rationale: z.string().trim().min(1).max(1_000),
});
export const ProblemSynthesisSchema = EncounterProblemSchema.extend({
    suggestedAssessment: SuggestedAssessmentSchema,
    suggestedDiagnosisCode: SuggestedDiagnosisCodeSchema,
    suggestedPlan: z.string().trim().min(1).max(6_000),
    /** One shared action may belong to several problems without being duplicated. */
    actionIndexes: z
        .array(z.number().int().min(0).max(19))
        .max(20)
        .refine((indexes) => new Set(indexes).size === indexes.length, "Action indexes must be unique within a problem"),
});
export const EncounterSynthesisSchema = z.object({
    clinicalSummary: z.string().trim().min(1).max(2_000),
    diagnosisCandidates: z.array(DiagnosisCandidateSchema).min(1).max(8),
    actions: z.array(PlanActionSchema).min(1).max(20),
    suggestedAssessment: SuggestedAssessmentSchema,
    suggestedDiagnosisCode: SuggestedDiagnosisCodeSchema,
    suggestedPlan: z.string().trim().min(1).max(6_000),
    problems: z.array(ProblemSynthesisSchema).max(ENCOUNTER_DIAGNOSES_MAX).optional(),
    unresolvedQuestions: ListSchema,
    redFlags: ListSchema,
    engine: CopilotEngineSchema,
});
const DictationSessionFields = {
    clientSecret: z.string().trim().min(16).max(4_096),
    expiresAt: z.number().int().positive(),
    model: z.string().trim().min(1).max(120),
    notice: z.string().trim().min(1).max(500),
};
export const DictationSessionResponseSchema = z.union([
    z.object({ ...DictationSessionFields, provider: z.literal("openai").optional() }).strict(),
    z
        .object({
        ...DictationSessionFields,
        provider: z.literal("assemblyai"),
        websocketUrl: z.enum([
            "wss://streaming.assemblyai.com/v3/ws",
            "wss://streaming.us.assemblyai.com/v3/ws",
            "wss://streaming.eu.assemblyai.com/v3/ws",
        ]),
        sampleRate: z.literal(16_000),
        medicalMode: z.boolean(),
    })
        .strict(),
]);
export const DictationAudioSchema = z
    .object({
    base64: z
        .string()
        .min(1)
        .max(34_000_000)
        .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/),
    mimeType: z
        .string()
        .trim()
        .regex(/^audio\/(?:webm|mp4|mpeg|mp3|mpga|m4a|x-m4a|wav|x-wav)(?:;.*)?$/i),
    sizeBytes: z.number().int().positive().max(24_000_000),
})
    .strict();
export const DictationSegmentSchema = z
    .object({
    speaker: z.string().trim().min(1).max(80),
    text: z.string().trim().min(1).max(4_000),
    startSeconds: z.number().min(0).max(86_400),
    endSeconds: z.number().min(0).max(86_400),
})
    .strict()
    .superRefine((value, context) => {
    if (value.endSeconds < value.startSeconds) {
        context.addIssue({
            code: "custom",
            message: "Segment end must not precede its start",
            path: ["endSeconds"],
        });
    }
});
export const DictationFinalizeRequestSchema = z
    .object({
    snapshot: EncounterSnapshotSchema,
    audio: DictationAudioSchema.optional(),
    transcript: TranscriptTextSchema.optional(),
    /** Earlier interrupted capture, prepended only after the new audio is transcribed. */
    priorTranscript: TranscriptTextSchema.optional(),
    segments: z.array(DictationSegmentSchema).min(1).max(2_000).optional(),
    followUpContext: DictationFollowUpContextSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.audio === undefined && value.transcript === undefined) {
        context.addIssue({
            code: "custom",
            message: "A recording or transcript is required",
            path: ["audio"],
        });
    }
    if (value.priorTranscript !== undefined && value.audio === undefined) {
        context.addIssue({
            code: "custom",
            message: "Prior transcript recovery requires a new audio recording; text-only requests must contain the combined transcript",
            path: ["priorTranscript"],
        });
    }
    if (value.segments !== undefined) {
        const canonical = (text) => text.replace(/\s+/g, " ").trim();
        if (value.audio !== undefined || value.transcript === undefined) {
            context.addIssue({
                code: "custom",
                message: "Live segments require a transcript and no recording",
                path: ["segments"],
            });
        }
        else if (canonical(value.segments.map((segment) => segment.text).join(" ")) !==
            canonical(value.transcript)) {
            context.addIssue({
                code: "custom",
                message: "Live segments must contain the complete transcript in order",
                path: ["segments"],
            });
        }
    }
});
export const SoapNoteDraftSchema = EncounterSoapNoteSchema.strict();
export const DictationFinalizeResponseSchema = z
    .object({
    transcript: TranscriptTextSchema,
    segments: z.array(DictationSegmentSchema).max(2_000),
    soap: SoapNoteDraftSchema,
    /** Grounded spoken decisions only; formatted or inferred A/P is never authority. */
    clinicianIntent: DictatedClinicianIntentSchema.optional(),
    topics: z.array(ClinicalTopicSchema).max(ClinicalTopicSchema.options.length),
    followUpProposals: z.array(DictationFollowUpProposalSchema).max(12).optional(),
    timings: z
        .object({
        transcriptionMs: z.number().nonnegative(),
        soapMs: z.number().nonnegative(),
        totalMs: z.number().nonnegative(),
    })
        .strict()
        .optional(),
    engine: CopilotEngineSchema,
    formattingFailure: DictationFormattingFailureSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.clinicianIntent !== undefined) {
        for (const section of ["assessment", "plan"]) {
            let previousEnd = -1;
            for (const [index, quote] of value.clinicianIntent.sourceQuotes[section].entries()) {
                const offset = value.transcript.indexOf(quote);
                if (!isCompleteDictationIntentQuote(value.transcript, quote) || offset < previousEnd) {
                    context.addIssue({
                        code: "custom",
                        message: "Spoken clinician intent requires unique, ordered, complete transcript clauses",
                        path: ["clinicianIntent", "sourceQuotes", section, index],
                    });
                }
                previousEnd = offset + quote.length;
            }
        }
        if (value.formattingFailure !== undefined &&
            (value.clinicianIntent.assessment || value.clinicianIntent.plan)) {
            context.addIssue({
                code: "custom",
                message: "Failed formatting cannot establish spoken clinician intent",
                path: ["clinicianIntent"],
            });
        }
    }
    const proposals = value.followUpProposals ?? [];
    if (new Set(proposals.map((proposal) => proposal.followUpId)).size !== proposals.length) {
        context.addIssue({
            code: "custom",
            message: "Follow-up proposal IDs must be unique",
            path: ["followUpProposals"],
        });
    }
    for (const [index, proposal] of proposals.entries()) {
        if (!value.transcript.includes(proposal.answer)) {
            context.addIssue({
                code: "custom",
                message: "Follow-up proposal answers must be exact transcript excerpts",
                path: ["followUpProposals", index, "answer"],
            });
        }
    }
});
export const PatientInstructionPlanActionSchema = PlanActionSchema.extend({
    kind: z.literal("patient_instruction"),
});
/** Handout languages the renderer can produce today; extend before adding a UI selector. */
export const PatientEducationLanguageSchema = z.enum(["en-CA"]);
const HandoutLineSchema = z.string().trim().min(1).max(300);
const handoutLines = (min, max) => z.array(HandoutLineSchema).min(min).max(max);
/**
 * Structured, plain-language patient education handout. The renderer draws the
 * PDF from these fields (the model never types into the PDF), and every field
 * lands in an editable form field so the physician reviews and edits each line
 * with the patient before printing.
 */
export const PatientEducationHandoutSchema = z
    .object({
    language: PatientEducationLanguageSchema,
    title: z.string().trim().min(1).max(160),
    whatWeFound: z.string().trim().min(1).max(1_200),
    careInstructions: handoutLines(1, 12),
    medicationInstructions: handoutLines(0, 8),
    returnPrecautions: handoutLines(1, 10),
    emergencyPrecautions: handoutLines(1, 8),
    followUp: z.string().trim().min(1).max(600),
})
    .strict();
export const DraftedPatientEducationSchema = z
    .object({
    handout: PatientEducationHandoutSchema,
    engine: CopilotEngineSchema,
})
    .strict();
/** Attending's display name for the printed header; the demo clinic identity is used when absent. */
const HandoutClinicianNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    // Printed as one header line; a newline would forge extra credential lines.
    .refine((value) => !/[\r\n]/.test(value), "Clinician name must be a single line");
export const PatientEducationRequestSchema = z
    .object({
    snapshot: EncounterSnapshotSchema,
    // Same ceiling as the synthesis and lab-requisition contracts, so a plan the
    // model is allowed to produce can never be rejected here.
    actions: z.array(PatientInstructionPlanActionSchema).min(1).max(20),
    language: PatientEducationLanguageSchema.default("en-CA"),
    clinicianName: HandoutClinicianNameSchema.optional(),
})
    .strict();
export const PatientEducationResponseSchema = DraftedPatientEducationSchema.extend({
    fileName: z.string().trim().min(1).max(240),
    mimeType: z.literal("application/pdf"),
    pdfBase64: z.string().min(1).max(5_000_000),
    template: z
        .object({
        id: z.literal("emr-patient-education-handout"),
        version: z.literal("2026-08"),
    })
        .strict(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    notice: z.string().trim().min(1).max(500),
}).strict();
//# sourceMappingURL=encounter-documentation.js.map